import { Check, ChevronDown, ChevronUp, Pencil, Search, Users, X } from 'lucide-react'
import * as React from 'react'
import { useId } from 'react'
import type { TranscriptSaveStatus } from '@/components/project/artifact-viewer/transcript/TranscriptSaveStatusIndicator'
import { TranscriptSaveStatusIndicator } from '@/components/project/artifact-viewer/transcript/TranscriptSaveStatusIndicator'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CollapsibleToolbar, ToolbarItem } from '@/components/ui/collapsible-toolbar'
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { ToolbarButton, ToolbarGroup, ToolbarSeparator } from '@/components/ui/toolbar'
import { useTranslation } from '@/hooks/useTranslation'

interface TranscriptFloatingBarProps {
	saveStatus: TranscriptSaveStatus
	hasUnsavedChanges: boolean
	onSave: () => void
	// Find/Replace
	searchQuery: string
	replaceQuery: string
	matchCount: number
	currentMatchIndex: number
	caseSensitive: boolean
	onCaseSensitiveChange: (value: boolean) => void
	onSearchChange: (value: string) => void
	onReplaceChange: (value: string) => void
	onPrev: () => void
	onNext: () => void
	onReplace: () => void
	onReplaceAll: () => void
	// Speakers
	speakers: string[]
	selectedSpeakers: Set<string>
	onSpeakerToggle: (speaker: string) => void
	onSelectAllSpeakers: () => void
	onRenameSpeaker?: (oldName: string, newName: string) => void
	onBatchRenameSpeakers?: (renames: Map<string, string>) => void
}

/**
 * TranscriptFloatingBar component
 * Floating toolbar for transcript editing with find/replace and speaker filtering
 */
export const TranscriptFloatingBar: React.FC<TranscriptFloatingBarProps> = ({
	saveStatus,
	hasUnsavedChanges,
	onSave,
	searchQuery,
	replaceQuery,
	matchCount,
	currentMatchIndex,
	caseSensitive,
	onCaseSensitiveChange,
	onSearchChange,
	onReplaceChange,
	onPrev,
	onNext,
	onReplace,
	onReplaceAll,
	speakers,
	selectedSpeakers,
	onSpeakerToggle,
	onSelectAllSpeakers,
	onRenameSpeaker,
	onBatchRenameSpeakers,
}) => {
	const { t } = useTranslation()
	const matchCaseId = useId()

	const [isEditingSpeakers, setIsEditingSpeakers] = React.useState(false)
	const [editedSpeakerNames, setEditedSpeakerNames] = React.useState<Map<string, string>>(new Map())

	const allSelected = speakers.length > 0 && selectedSpeakers.size === speakers.length
	const hasSearch = searchQuery.length > 0

	// Reset edited names when entering edit mode
	const handleStartEditing = () => {
		setEditedSpeakerNames(new Map())
		setIsEditingSpeakers(true)
	}

	const handleCancelEditing = () => {
		setEditedSpeakerNames(new Map())
		setIsEditingSpeakers(false)
	}

	const handleSaveEditing = () => {
		// Build a clean rename map (only actual changes)
		const cleanRenames = new Map<string, string>()
		for (const [oldName, newName] of editedSpeakerNames) {
			const trimmed = newName.trim()
			if (trimmed && trimmed !== oldName) {
				cleanRenames.set(oldName, trimmed)
			}
		}

		if (cleanRenames.size > 0) {
			if (onBatchRenameSpeakers) {
				// Preferred: apply all renames atomically
				onBatchRenameSpeakers(cleanRenames)
			} else if (onRenameSpeaker) {
				// Fallback: sequential renames (legacy)
				for (const [oldName, newName] of cleanRenames) {
					onRenameSpeaker(oldName, newName)
				}
			}
		}

		setEditedSpeakerNames(new Map())
		setIsEditingSpeakers(false)
	}

	const handleSpeakerNameChange = (originalName: string, newName: string) => {
		setEditedSpeakerNames((prev) => {
			const next = new Map(prev)
			if (newName === originalName) {
				next.delete(originalName)
			} else {
				next.set(originalName, newName)
			}
			return next
		})
	}

	const hasChanges = editedSpeakerNames.size > 0

	return (
		<CollapsibleToolbar position="bottom-right" canCollapse={false}>
			{/* Save Status */}
			<ToolbarGroup>
				<ToolbarItem>
					<TranscriptSaveStatusIndicator saveStatus={saveStatus} />
				</ToolbarItem>

				{hasUnsavedChanges && (
					<ToolbarItem>
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={onSave}
							disabled={saveStatus === 'saving'}
							className="h-7 px-3 text-xs"
						>
							{t('pages.projectDetail.transcript.save')}
						</Button>
					</ToolbarItem>
				)}

				<ToolbarItem>
					<ToolbarSeparator className="bg-gray-200" />
				</ToolbarItem>
			</ToolbarGroup>

			{/* Speaker Filter */}
			{speakers.length > 0 && (
				<>
					<ToolbarGroup>
						<ToolbarItem>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<ToolbarButton
										variant="ghost"
										className="h-7 px-2 text-xs rounded-full hover:bg-[#8c52ff]/10 hover:text-[#8c52ff]"
									>
										<Users className="w-4 h-4 mr-1" />
										{t('pages.projectDetail.transcript.speakers')}
										{!allSelected && <span className="ml-1 text-[#8c52ff]">({selectedSpeakers.size})</span>}
									</ToolbarButton>
								</DropdownMenuTrigger>

								<DropdownMenuContent
									side="top"
									align="start"
									sideOffset={8}
									className="min-w-[220px]"
									onCloseAutoFocus={(e) => e.preventDefault()}
								>
									<div className="flex items-center justify-between px-2 py-1.5">
										<DropdownMenuLabel className="p-0">
											{isEditingSpeakers
												? t('pages.projectDetail.transcript.floatingBar.editSpeakers')
												: t('pages.projectDetail.transcript.filterBySpeaker')}
										</DropdownMenuLabel>

										{onRenameSpeaker && (
											<div className="flex items-center gap-1">
												{isEditingSpeakers ? (
													<>
														<Button
															type="button"
															variant="ghost"
															size="icon"
															onClick={handleCancelEditing}
															className="h-6 w-6"
														>
															<X className="w-3.5 h-3.5" />
														</Button>

														<Button
															type="button"
															variant="ghost"
															size="icon"
															onClick={handleSaveEditing}
															disabled={!hasChanges}
															className="h-6 w-6 text-[#8c52ff] hover:text-[#8c52ff] hover:bg-[#8c52ff]/10"
														>
															<Check className="w-3.5 h-3.5" />
														</Button>
													</>
												) : (
													<Button
														type="button"
														variant="ghost"
														size="icon"
														onClick={handleStartEditing}
														className="h-6 w-6"
													>
														<Pencil className="w-3.5 h-3.5" />
													</Button>
												)}
											</div>
										)}
									</div>

									<DropdownMenuSeparator />

									{isEditingSpeakers ? (
										<div className="p-2 space-y-2">
											{speakers.map((speaker) => (
												<div key={speaker} className="flex items-center gap-2">
													<Input
														type="text"
														value={editedSpeakerNames.get(speaker) ?? speaker}
														onChange={(e) => handleSpeakerNameChange(speaker, e.target.value)}
														className="h-7 text-sm"
														aria-label={`${t('pages.projectDetail.transcript.floatingBar.editSpeakerName')} ${speaker}`}
													/>
												</div>
											))}
										</div>
									) : (
										<>
											<DropdownMenuCheckboxItem checked={allSelected} onCheckedChange={onSelectAllSpeakers}>
												{t('pages.projectDetail.transcript.floatingBar.selectAll')}
											</DropdownMenuCheckboxItem>

											<DropdownMenuSeparator />

											{speakers.map((speaker) => (
												<DropdownMenuCheckboxItem
													key={speaker}
													checked={selectedSpeakers.has(speaker)}
													onCheckedChange={() => onSpeakerToggle(speaker)}
												>
													{speaker}
												</DropdownMenuCheckboxItem>
											))}
										</>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						</ToolbarItem>
					</ToolbarGroup>

					<ToolbarItem>
						<ToolbarSeparator className="bg-gray-200" />
					</ToolbarItem>
				</>
			)}

			{/* Find/Replace Drop-up */}
			<ToolbarGroup>
				<ToolbarItem>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<ToolbarButton
								variant="ghost"
								className="h-7 px-2 text-xs rounded-full hover:bg-[#8c52ff]/10 hover:text-[#8c52ff]"
							>
								<Search className="w-4 h-4 mr-1" />
								{t('pages.projectDetail.transcript.find')}
								{hasSearch && matchCount > 0 && (
									<span className="ml-1 text-[#8c52ff]">
										({currentMatchIndex + 1}/{matchCount})
									</span>
								)}
							</ToolbarButton>
						</DropdownMenuTrigger>

						<DropdownMenuContent
							side="top"
							align="end"
							sideOffset={8}
							className="w-72 p-3"
							onCloseAutoFocus={(e) => e.preventDefault()}
						>
							<div className="space-y-3">
								{/* Find */}
								<div className="space-y-1.5">
									<label
										htmlFor="transcript-find-input"
										className="text-xs font-medium text-muted-foreground"
									>
										{t('pages.projectDetail.transcript.find')}
									</label>

									<div className="flex items-center gap-1">
										<Input
											id="transcript-find-input"
											type="text"
											placeholder={t('pages.projectDetail.transcript.searchPlaceholder')}
											value={searchQuery}
											onChange={(e) => onSearchChange(e.target.value)}
											className="h-8 text-sm flex-1"
										/>

										<Button
											type="button"
											variant="ghost"
											size="icon"
											onClick={onPrev}
											disabled={matchCount === 0}
											className="h-8 w-8 shrink-0"
										>
											<ChevronUp className="w-4 h-4" />
										</Button>

										<Button
											type="button"
											variant="ghost"
											size="icon"
											onClick={onNext}
											disabled={matchCount === 0}
											className="h-8 w-8 shrink-0"
										>
											<ChevronDown className="w-4 h-4" />
										</Button>
									</div>

									<label
										className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground"
										htmlFor={matchCaseId}
									>
										<Checkbox
											id={matchCaseId}
											checked={caseSensitive}
											onChange={(e) => onCaseSensitiveChange(e.target.checked)}
											aria-label={t('pages.projectDetail.transcript.floatingBar.matchCase')}
										/>
										{t('pages.projectDetail.transcript.floatingBar.matchCase')}
									</label>

									{matchCount > 0 && (
										<p className="text-xs text-muted-foreground">
											{currentMatchIndex + 1} / {matchCount}{' '}
											{t('pages.projectDetail.transcript.floatingBar.matches')}
										</p>
									)}
								</div>

								<DropdownMenuSeparator />

								{/* Replace */}
								<div className="space-y-1.5">
									<label
										htmlFor="transcript-replace-input"
										className="text-xs font-medium text-muted-foreground"
									>
										{t('pages.projectDetail.transcript.replace')}
									</label>

									<Input
										id="transcript-replace-input"
										type="text"
										placeholder={t('pages.projectDetail.transcript.replacePlaceholder')}
										value={replaceQuery}
										onChange={(e) => onReplaceChange(e.target.value)}
										className="h-8 text-sm"
									/>

									<div className="flex gap-2 pt-1">
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={onReplace}
											disabled={matchCount === 0}
											className="flex-1 h-8"
										>
											{t('pages.projectDetail.transcript.replace')}
										</Button>

										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={onReplaceAll}
											disabled={matchCount === 0}
											className="flex-1 h-8"
										>
											{t('pages.projectDetail.transcript.replaceAll')}
										</Button>
									</div>
								</div>
							</div>
						</DropdownMenuContent>
					</DropdownMenu>
				</ToolbarItem>
			</ToolbarGroup>
		</CollapsibleToolbar>
	)
}
